// Public clinic self-registration. Protected by the same anti-bot challenge + honeypot as the login
// form.
//
// Seven fields, and every one of them is something the doctor actually knows. The clinic code is not:
// it is the lab's filing reference, so the server allocates it. Nor is an email address, which this
// lab never writes to - it reaches clinics by phone and WhatsApp.
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { clinicSignupSchema } from '@alignova/shared';
import { applyAudience } from '../theme/useAudienceTheme.ts';
import { api, ApiError } from '../lib/api.ts';
import { PasswordInput } from '../ui/PasswordInput.tsx';
import { solveChallenge } from '../lib/pow.ts';
import { AlignovaLockup, BRAND_BLUE } from '../ui/AlignovaLogo.tsx';

interface Challenge {
  token: string | null;
  difficulty: number;
}

const inputClass =
  'w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#1668A8] focus:ring-2 focus:ring-[#1668A8]/20';

export function ClinicSignup() {
  applyAudience('staff');
  const [f, setF] = useState({ name: '', dentistName: '', mobile: '', whatsapp: '', city: '', username: '', password: '' });
  const [website, setWebsite] = useState(''); // honeypot
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF((p) => ({ ...p, [k]: e.target.value }));

  const fetchChallenge = () => {
    api.get<Challenge>('/auth/challenge').then(setChallenge).catch(() => setChallenge({ token: null, difficulty: 0 }));
  };
  useEffect(() => {
    fetchChallenge();
    // Server tokens live 5 minutes; refresh well inside that so an idle page still submits cleanly.
    const id = setInterval(fetchChallenge, 4 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const parsed = clinicSignupSchema.safeParse(f);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Please check the form');
      return;
    }
    setBusy(true);
    try {
      let solved: { token: string; solution: string } | undefined;
      if (challenge?.token) solved = { token: challenge.token, solution: await solveChallenge(challenge.token, challenge.difficulty) };
      await api.post('/auth/clinic-signup', { ...parsed.data, website, challenge: solved });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : 'Registration failed. Please try again.');
      fetchChallenge();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-dvh w-full items-center justify-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <AlignovaLockup sub="Clinic registration" />
        {done ? (
          <div className="mt-6">
            <h1 className="text-xl font-bold text-slate-900">Your clinic is registered</h1>
            <p className="mt-2 text-sm text-slate-600">
              Sign in with the username and password you just chose. The lab will be in touch on the
              WhatsApp number you gave.
            </p>
            <Link to="/login" className="mt-6 inline-block rounded-lg px-4 py-2 text-sm font-semibold text-white" style={{ backgroundColor: BRAND_BLUE }}>
              Sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="mt-6 text-xl font-bold text-slate-900">Register your clinic</h1>
            <p className="mt-1 text-sm text-slate-500">Create your provider portal account. You can sign in as soon as you have submitted this.</p>
            <form onSubmit={onSubmit} className="mt-6 grid gap-4 sm:grid-cols-2" autoComplete="on">
              <Field label="Clinic name" full><input className={inputClass} value={f.name} onChange={set('name')} required /></Field>
              <Field label="Dentist name"><input className={inputClass} value={f.dentistName} onChange={set('dentistName')} required /></Field>
              <Field label="Mobile"><input className={inputClass} type="tel" value={f.mobile} onChange={set('mobile')} autoComplete="tel" required /></Field>
              <Field label="WhatsApp number"><input className={inputClass} type="tel" value={f.whatsapp} onChange={set('whatsapp')} required /></Field>
              <Field label="City" full><input className={inputClass} value={f.city} onChange={set('city')} required /></Field>
              <Field label="Portal username" hint="What you sign in with"><input className={inputClass} value={f.username} onChange={set('username')} autoComplete="username" required /></Field>
              <Field label="Password" hint="At least 8 characters"><PasswordInput className={inputClass} value={f.password} onChange={set('password')} autoComplete="new-password" required /></Field>

              <div aria-hidden="true" className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden">
                <label htmlFor="website">Website</label>
                <input id="website" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
              </div>

              {error && (
                <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 sm:col-span-2" role="alert">
                  {error}
                </p>
              )}
              <button type="submit" disabled={busy} className="rounded-lg py-2.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-110 disabled:opacity-60 sm:col-span-2" style={{ backgroundColor: BRAND_BLUE }}>
                {busy ? 'Submitting…' : 'Create account'}
              </button>
            </form>
            <p className="mt-5 text-center text-sm text-slate-500">
              Already registered?{' '}
              <Link to="/login" className="font-semibold hover:underline" style={{ color: BRAND_BLUE }}>
                Sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, hint, full, children }: { label: string; hint?: string; full?: boolean; children: React.ReactNode }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}
